/**
 * DLC Dialogs for GameDetail
 * - Add DLC Dialog (create new DLC)
 * - Edit DLC Dialog (edit existing DLC with IGDB search)
 */
import { useState, useEffect, useCallback } from "react";
import {
  Loader2,
  Plus,
  Search,
  Image,
  Database,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import ImageUpload from "@/components/ImageUpload";
import { toast } from "sonner";
import axios, { getApiUrl } from "@/lib/api";

const API = `${getApiUrl()}/api`;

/**
 * Add DLC Dialog - Create a new DLC
 */
export function AddDlcDialog({
  open,
  onOpenChange,
  dlc,
  onDlcChange,
  onSave,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-white/20">
          <Plus className="w-4 h-4 mr-1" />
          Add DLC
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle>Add DLC</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>DLC Name</Label>
            <Input
              placeholder="e.g., Season Pass, Story DLC"
              value={dlc.name}
              onChange={(e) => onDlcChange({ ...dlc, name: e.target.value })}
              className="bg-black/50 border-white/10"
            />
          </div>
          <div className="space-y-2">
            <Label>Release Date (optional)</Label>
            <Input
              type="date"
              value={dlc.releaseDate}
              onChange={(e) => onDlcChange({ ...dlc, releaseDate: e.target.value })}
              className="bg-black/50 border-white/10"
            />
          </div>
          <div className="space-y-2">
            <Label>Completion Estimate (optional)</Label>
            <Input
              placeholder="e.g., 5-10 hours"
              value={dlc.completionEstimate || ""}
              onChange={(e) => onDlcChange({ ...dlc, completionEstimate: e.target.value })}
              className="bg-black/50 border-white/10"
            />
          </div>
          <div className="space-y-2">
            <Label>Box Art (optional)</Label>
            <div className="flex items-start gap-3">
              <ImageUpload
                folder="gamepop/dlcs"
                currentImageUrl={dlc.boxArtUrl}
                onImageUploaded={(url) => onDlcChange({ ...dlc, boxArtUrl: url })}
                size="sm"
              />
              <Input
                placeholder="Or paste URL..."
                value={dlc.boxArtUrl}
                onChange={(e) => onDlcChange({ ...dlc, boxArtUrl: e.target.value })}
                className="bg-black/50 border-white/10 flex-1"
              />
            </div>
          </div>
          <Button onClick={onSave} className="w-full bg-white text-black hover:bg-gray-200">
            Add DLC
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Edit DLC Dialog - Edit existing DLC with IGDB search
 */
export function EditDlcDialog({
  open,
  onOpenChange,
  dlc,
  onDlcChange,
  onSave,
  gameId,
}) {
  const [igdbSearch, setIgdbSearch] = useState("");
  const [igdbResults, setIgdbResults] = useState([]);
  const [igdbLoading, setIgdbLoading] = useState(false);

  // Clear IGDB state when dialog closes
  const handleOpenChange = (newOpen) => {
    if (!newOpen) {
      setIgdbSearch("");
      setIgdbResults([]);
    }
    onOpenChange(newOpen);
  };

  // Search IGDB for DLC images
  const searchIgdb = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setIgdbResults([]);
      return;
    }

    setIgdbLoading(true);
    try {
      const response = await axios.get(`${API}/igdb/search-dlcs`, {
        params: { query: searchTerm },
        withCredentials: true,
      });
      setIgdbResults(response.data || []);
    } catch (err) {
      console.error("IGDB DLC search error:", err);
      setIgdbResults([]);
    } finally {
      setIgdbLoading(false);
    }
  }, []);

  // Debounced search effect
  useEffect(() => {
    if (!dlc) return;
    
    const timeoutId = setTimeout(() => {
      if (igdbSearch) {
        searchIgdb(igdbSearch);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [igdbSearch, dlc, searchIgdb]);

  if (!dlc) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit DLC</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label>DLC Name</Label>
            <Input
              placeholder="e.g., Season Pass, Story DLC"
              value={dlc.name}
              onChange={(e) => onDlcChange({ ...dlc, name: e.target.value })}
              className="bg-black/50 border-white/10"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Box Art</Label>
            <div className="flex items-start gap-3">
              <ImageUpload
                folder="gamepop/dlcs"
                currentImageUrl={dlc.boxArtUrl || ""}
                onImageUploaded={(url) => onDlcChange({ ...dlc, boxArtUrl: url })}
                size="sm"
              />
              <div className="flex-1">
                <Input
                  placeholder="Or paste URL..."
                  value={dlc.boxArtUrl || ""}
                  onChange={(e) => onDlcChange({ ...dlc, boxArtUrl: e.target.value })}
                  className="bg-black/50 border-white/10"
                />
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Release Date</Label>
            <Input
              type="date"
              value={dlc.releaseDate || ""}
              onChange={(e) => onDlcChange({ ...dlc, releaseDate: e.target.value })}
              className="bg-black/50 border-white/10"
            />
            <p className="text-xs text-white/40">
              Used for sorting DLCs chronologically. Leave empty to sort alphabetically.
            </p>
          </div>
          
          <div className="space-y-2">
            <Label>Completion Estimate</Label>
            <Input
              placeholder="e.g., 5-10 hours"
              value={dlc.completionEstimate || ""}
              onChange={(e) => onDlcChange({ ...dlc, completionEstimate: e.target.value })}
              className="bg-black/50 border-white/10"
            />
            <p className="text-xs text-white/40">
              Estimated time to complete this DLC (e.g., &quot;5-10 hours&quot;, &quot;20 hours&quot;).
            </p>
          </div>
          
          {/* IGDB Search Section */}
          <div className="space-y-2 border-t border-white/10 pt-4">
            <Label className="flex items-center gap-2">
              <Database className="w-4 h-4 text-purple-400" />
              Search IGDB for Image
            </Label>
            <div className="flex gap-2">
              <Input
                placeholder={`Search "${dlc.name}" on IGDB...`}
                value={igdbSearch}
                onChange={(e) => setIgdbSearch(e.target.value)}
                className="bg-black/50 border-white/10 flex-1"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIgdbSearch(dlc.name)}
                className="border-white/20 whitespace-nowrap"
              >
                <Search className="w-4 h-4 mr-1" />
                Search
              </Button>
            </div>
            
            {/* IGDB Results */}
            {igdbLoading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-white/50" />
                <span className="ml-2 text-white/50 text-sm">Searching IGDB...</span>
              </div>
            )}
            
            {!igdbLoading && igdbResults.length > 0 && (
              <div className="max-h-48 overflow-y-auto space-y-2 mt-2">
                {igdbResults.map((result) => (
                  <button
                    key={result.id}
                    onClick={() => {
                      if (result.cover_url) {
                        onDlcChange({ ...dlc, boxArtUrl: result.cover_url });
                        toast.success(`Image selected from "${result.name}"`);
                      } else {
                        toast.error("No image available for this DLC");
                      }
                    }}
                    className="w-full flex items-center gap-3 p-2 bg-[#121212] rounded-lg border border-white/10 hover:border-purple-500/50 hover:bg-[#181818] transition-all text-left"
                  >
                    {result.cover_url ? (
                      <img 
                        src={result.cover_url} 
                        alt={result.name}
                        className="w-12 h-12 rounded object-contain bg-black/50 flex-shrink-0"
                        onError={(e) => { e.target.src = '/placeholder-game.png'; }}
                      />
                    ) : (
                      <div className="w-12 h-12 rounded bg-white/10 flex items-center justify-center flex-shrink-0">
                        <Image className="w-6 h-6 text-white/30" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{result.name}</p>
                      <p className="text-white/40 text-xs truncate">
                        {result.parent_game && `${result.parent_game} • `}
                        {result.category}
                        {result.released && ` • ${result.released}`}
                      </p>
                    </div>
                    {result.cover_url && (
                      <span className="text-purple-400 text-xs">Select</span>
                    )}
                  </button>
                ))}
              </div>
            )}
            
            {!igdbLoading && igdbSearch && igdbResults.length === 0 && (
              <p className="text-white/40 text-sm text-center py-2">
                No DLCs found. Try a different search term.
              </p>
            )}
          </div>
          
          <Button onClick={onSave} className="w-full bg-white text-black hover:bg-gray-200">
            Save DLC
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
